import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { District, DistrictEditResource, DistrictViewResource, FileCategory, Permission } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { FileCategoryPageBody } from "./FileCategoryPageBody";
import { FileCategoryPageHeader } from "./FileCategoryPageHeader";

const routeName = "fileCategory";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

export const fileCategoryPageRoute: PageRoute<Params> = {
  handle: "File Category",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const FileCategoryPage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const [fileCategory, setFileCategory] = useState<FileCategory>({} as FileCategory);
  const [initFileCategory, setInitFileCategory] = useState<FileCategory>(fileCategory);
  const [viewResource, setViewResource] = useState<DistrictViewResource>({});
  const [editResource, setEditResource] = useState<DistrictEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [fileCategory] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.fileCategories.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.fileCategories.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${fileCategoryPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType,  })}`);

  const fetch = (id: number) => {
    ApiService.fileCategories.get(id).then((response) => {
      setFileCategory(response);
      setInitFileCategory(response);
      resetUnsavedChanges([response]);
    });
  };

  return (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={
        <FileCategoryPageHeader
          rootPermission={ROOT_PERMISSION}
          handle={fileCategoryPageRoute.handle}
          crudMode={params.crudMode}
          reroute={reroute}
          fetch={fetch}
          fileCategory={fileCategory}
          setFileCategory={setFileCategory}
          initFileCategory={initFileCategory}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
          resetUnsavedChanges={resetUnsavedChanges}
        />
      }
      content={
        <FileCategoryPageBody
          fileCategory={fileCategory}
          setFileCategory={setFileCategory}
          viewResource={viewResource}
          editResource={editResource}
          crudMode={params.crudMode}
          validationErrors={validationErrors}
        />
      }
    />
  );
};
